import { useCallback, useEffect, useState } from "react";

import {
  type DataPointSample,
  type Equipment,
  listZoneFindings,
  type Finding,
} from "@/lib/api/building";
import { cn } from "@/lib/utils";
import { cardSubtle } from "@/lib/card-styles";
import {
  DATA_POINT_DOMAIN_COLOR,
  DATA_POINT_DOMAIN_LABEL,
  DOMAIN_ORDER,
  type DataPoint,
  type DataPointDomain,
  type Zone,
} from "@/lib/building-domain";
import {
  getKBTypes,
  getZoneEquipmentSignals,
  type KBZoneSummary,
  type ZoneEquipmentSignal,
} from "@/lib/api/agent";
import { ZoneKBBehaviorList } from "@/components/configuration/KBBehaviorList";
import { FindingsList } from "../findings/FindingsList";
import { OPEN_AND_ACKNOWLEDGED } from "../findings/findings-helpers";
import { DataPointsExplorer } from "../datapoints/DataPointsExplorer";
import { build_zone_visible_signals } from "./zone-signal-filter";

// ── Helpers ───────────────────────────────────────────────────────────────────

// Shared by ZoneEquipmentSignalTile and the raw-data-point tile below — both render a
// small bordered stat tile with a colored left rail, differing only in the rail color.
const signalTileClass = cn(
  cardSubtle,
  "flex flex-col gap-1 border-border/30 bg-background/60 px-3 py-2.5",
);

function floor_label(floor: number): string {
  if (floor === 0) return "Rakennustaso";
  return `Kerros ${floor}`;
}

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface PanelProps {
  buildingId: string;
  zone: Zone;
  equipment: Equipment[];
  // This zone's own data_point_history samples, grouped by KB data_point name via
  // zoneSignalSeries — replaces the frozen temperature/ventilation_rate/lighting_level/
  // occupancy/energy_use dummy fields (#1451).
  live_signals: Record<string, DataPointSample[]>;
  /** @deprecated DataPointsExplorer derives labels from the KB internally. */
  signal_labels?: Record<string, string>;
  on_close: () => void;
  on_update?: (
    zone_id: string,
    data: { zone_type?: string | null },
  ) => Promise<void>;
  operator?: boolean;
  is_list?: boolean;
}

interface AnnotatedPoint {
  dp: DataPoint;
  source: string;
}

// ── ZoneDetailPanel ───────────────────────────────────────────────────────────

export function ZoneDetailPanel({
  buildingId,
  zone,
  equipment,
  live_signals,
  on_close,
  on_update,
  operator = false,
  is_list = false,
}: PanelProps) {
  const [findings, set_findings] = useState<Finding[]>([]);
  const [findings_loading, set_findings_loading] = useState(true);
  const [equipment_signals, set_equipment_signals] = useState<
    ZoneEquipmentSignal[]
  >([]);
  const [kb_zone_types, set_kb_zone_types] = useState<KBZoneSummary[]>([]);
  const [local_zone_type, set_local_zone_type] = useState(zone.zone_type ?? "");
  // Only updated after a successful PATCH — gates ZoneKBBehaviorList mount
  const [saved_zone_type, set_saved_zone_type] = useState(zone.zone_type ?? "");
  const [saving_type, set_saving_type] = useState(false);
  // Increment to force ZoneKBBehaviorList remount after type change
  const [kb_key, set_kb_key] = useState(0);

  useEffect(() => {
    set_findings([]);
    set_findings_loading(true);
    listZoneFindings(buildingId, zone.id)
      .then(set_findings)
      .catch(() => {})
      .finally(() => set_findings_loading(false));
  }, [buildingId, zone.id]);

  useEffect(() => {
    if (!operator) return;
    getKBTypes()
      .then((r) => set_kb_zone_types(r.zone_types))
      .catch(() => {});
  }, [operator]);

  useEffect(() => {
    set_equipment_signals([]);
    if (operator) return;
    getZoneEquipmentSignals(buildingId, zone.id)
      .then((r) => set_equipment_signals(r.signals))
      .catch(() => {});
  }, [buildingId, zone.id, operator]);

  async function handle_zone_type_change(new_type: string) {
    if (!on_update || saving_type) return;
    set_local_zone_type(new_type);
    set_saving_type(true);
    try {
      await on_update(zone.id, { zone_type: new_type || null });
      set_saved_zone_type(new_type);
      set_kb_key((k) => k + 1);
    } catch {
      set_local_zone_type(saved_zone_type);
    } finally {
      set_saving_type(false);
    }
  }

  const resolve_zone_visible_signals = useCallback(
    () =>
      build_zone_visible_signals(
        zone.id,
        Object.keys(live_signals),
        equipment_signals,
      ),
    [zone.id, live_signals, equipment_signals],
  );

  const all_points: AnnotatedPoint[] = equipment.flatMap((e) => {
    const raw = e.state["data_points"];
    if (!Array.isArray(raw)) return [];
    const source = e.equipment_tag || e.name;
    return (raw as DataPoint[]).map((dp) => ({ dp, source }));
  });

  const by_category = DOMAIN_ORDER.map((dom) => ({
    cat: dom,
    label: DATA_POINT_DOMAIN_LABEL[dom],
    color: DATA_POINT_DOMAIN_COLOR[dom],
    points: all_points.filter(({ dp }) => dp.domain === dom),
  })).filter((group) => group.points.length > 0);

  return (
    <div
      className={cn(!is_list && "col-span-full", cardSubtle, "bg-card/60 p-4")}
    >
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold">{zone.name}</h3>
          <p className="text-xs text-muted-foreground">
            {floor_label(zone.floor)}
          </p>
          {zone.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {zone.description}
            </p>
          )}
        </div>
        <button
          onClick={on_close}
          className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          ✕
        </button>
      </div>

      {operator ? (
        /* Config mode: zone type selector + Odotettu toiminta from KB */
        <>
          {on_update && kb_zone_types.length > 0 && (
            <div className="mb-3">
              <label className="mb-1 block text-xs text-muted-foreground">
                Vyöhyketyyppi
              </label>
              <select
                value={local_zone_type}
                onChange={(e) => void handle_zone_type_change(e.target.value)}
                disabled={saving_type}
                className="w-full rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60 disabled:opacity-50"
              >
                <option value="">— Ei tyyppiä —</option>
                {kb_zone_types.map((zt) => (
                  <option key={zt.id} value={zt.id}>
                    {zt.name_fi}
                  </option>
                ))}
              </select>
            </div>
          )}
          {saved_zone_type ? (
            <ZoneKBBehaviorList
              key={kb_key}
              buildingId={buildingId}
              zoneId={zone.id}
            />
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Valitse vyöhyketyyppi nähdäksesi odotetun toiminnan.
            </p>
          )}
        </>
      ) : (
        /* Monitoring mode: live signals → findings → equipment data points */
        <>
          <section className="mb-5">
            <DataPointsExplorer
              buildingId={buildingId}
              resolveVisibleSignals={resolve_zone_visible_signals}
            />
          </section>

          <FindingsList
            findings={findings}
            loading={findings_loading}
            sourceId={zone.id}
            defaultStatuses={OPEN_AND_ACKNOWLEDGED}
            showStatusFilter={false}
            emptyMessage="Ei avoimia havaintoja."
            onAcknowledged={(updated) =>
              set_findings((prev) =>
                prev.map((f) => (f.id === updated.id ? updated : f)),
              )
            }
          />

          {by_category.length > 0 && (
            <div className="mt-5 flex flex-col gap-4">
              {by_category.map(
                ({
                  cat,
                  label,
                  color,
                  points,
                }: {
                  cat: DataPointDomain;
                  label: string;
                  color: string;
                  points: AnnotatedPoint[];
                }) => (
                  <div key={cat}>
                    <p
                      className="mb-2 text-xs font-medium uppercase tracking-wide"
                      style={{ color }}
                    >
                      {label}
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {points.map(({ dp, source }, i) => (
                        <div
                          key={i}
                          className={signalTileClass}
                          style={{ borderLeft: `3px solid ${color}` }}
                        >
                          <span className="text-xs text-muted-foreground">
                            {dp.name}
                          </span>
                          <span className="text-sm font-semibold leading-tight">
                            {dp.value}
                          </span>
                          <span className="font-mono text-xs text-muted-foreground/60">
                            {source}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
